import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Minus, Plus, Trash2, Package, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ReportButton } from "@/components/ReportButton";
import { useCart } from "@/context/CartContext";
import { Page, Avatar, Empty } from "@/components/layout/AppShell";
import { ProductCard } from "@/components/Cards";
import { absUrl } from "@/components/Uploader";

const MARKET_CATEGORIES = ["Guides", "Decks", "Books", "Music", "Courses", "Spreads", "Art & Prints", "Candles & Ritual", "Journals"];

export default function Market() {
  const [sp, setSp] = useSearchParams();
  const kind = sp.get("kind") || "";
  const q = sp.get("q") || "";
  const category = sp.get("category") || "";
  const [items, setItems] = useState<any[]>([]);
  useEffect(() => { api.get("/products", { params: { kind: kind || undefined, q: q || undefined, category: category || undefined } }).then((r) => setItems(r.data)); }, [kind, q, category]);
  const set = (k: string, v: string) => { const n = new URLSearchParams(sp); v ? n.set(k, v) : n.delete(k); setSp(n); };
  return (
    <Page>
      <p className="eyebrow mb-3">Market</p>
      <h1 className="text-4xl sm:text-5xl font-semibold leading-none mb-6">Decks, books, music and tools<br />from readers and creators you follow.</h1>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {[["", "All"], ["digital", "Digital"], ["physical", "Physical"]].map(([k, l]) => <button key={k} onClick={() => set("kind", k)} className={`chip ${kind === k ? "chip-on" : ""}`} data-testid={`market-filter-${k || "all"}`}>{l}</button>)}
        <input className="input-ed !w-auto ml-auto" placeholder="Search products" value={q} onChange={(e) => set("q", e.target.value)} data-testid="market-search" />
      </div>
      <div className="flex gap-2 overflow-x-auto scrollbar-none mb-6" data-testid="market-categories">
        {MARKET_CATEGORIES.map((c) => <button key={c} onClick={() => set("category", category === c ? "" : c)} className={`chip whitespace-nowrap ${category === c ? "chip-on" : ""}`} data-testid={`market-category-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{c}</button>)}
      </div>
      {items.length === 0 ? <Empty title="No products yet." /> : <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="market-grid">{items.map((p) => <ProductCard key={p.id} p={p} />)}</div>}
    </Page>
  );
}

export function ProductPage() {
  const { id } = useParams();
  const { add } = useCart();
  const nav = useNavigate();
  const [p, setP] = useState<any>(null);
  const [variant, setVariant] = useState<string>("");
  useEffect(() => { api.get(`/products/${id}`).then((r) => { setP(r.data); setVariant(r.data.variants?.[0] || ""); }); }, [id]);
  if (!p) return <Page><div className="card-tactile h-64 animate-pulse" /></Page>;
  const addToCart = () => {
    const err = add({ product_id: p.id, title: p.title, price: p.price, kind: p.kind, image_url: p.image_url, reader_id: p.reader_id, reader_name: p.reader?.name, quantity: 1, variant: variant || null, shipping_fee: p.shipping_fee });
    if (err) return toast.error(err);
    toast.success("Added to cart", { action: { label: "View cart", onClick: () => nav("/cart") } });
  };
  const soldOut = p.kind === "physical" && p.inventory !== null && p.inventory <= 0;
  return (
    <Page className="max-w-5xl">
      <div className="grid md:grid-cols-2 gap-8">
        <div className="aspect-square rounded-md overflow-hidden bg-[#1A1917] border border-[#D8D4C9]">{p.image_url ? <img src={absUrl(p.image_url)} alt={p.title} className="h-full w-full object-cover" /> : <div className="h-full w-full flex items-center justify-center text-[#C5A059]">{p.kind === "digital" ? <FileDown size={48} /> : <Package size={48} />}</div>}</div>
        <div>
          <p className="eyebrow mb-3">{p.kind} · {p.category}</p>
          <h1 className="text-4xl sm:text-5xl font-semibold leading-none" data-testid="product-title">{p.title}</h1>
          <Link to={`/@${p.reader?.username}`} className="flex items-center gap-2 mt-4 w-fit text-sm"><Avatar u={p.reader} size={28} />{p.reader?.name}</Link>
          <p className="mono text-3xl font-semibold mt-6" data-testid="product-price">{money(p.price)}</p>
          {p.kind === "physical" && <p className="mono text-xs text-[#5E5C56] mt-1">+ {money(p.shipping_fee || 0)} shipping · ships from {p.ships_from || "—"}{p.inventory !== null ? ` · ${p.inventory} left` : ""}</p>}
          {p.kind === "digital" && <p className="mono text-xs text-[#5E5C56] mt-1">Instant delivery to your Library</p>}
          {p.variants?.length > 0 && (
            <div className="mt-5"><p className="eyebrow mb-2">Variant</p><div className="flex flex-wrap gap-2">{p.variants.map((v: string) => <button key={v} onClick={() => setVariant(v)} className={`chip ${variant === v ? "chip-on" : ""}`} data-testid={`variant-${v}`}>{v}</button>)}</div></div>)}
          <p className="mt-6 text-[15px] leading-relaxed text-[#2C2A28] whitespace-pre-wrap">{p.description}</p>
          <button onClick={addToCart} disabled={soldOut} className="btn-wine mt-8 w-full sm:w-auto" data-testid="add-to-cart-btn">{soldOut ? "Sold out" : "Add to cart"}</button>
          <div className="mt-4"><ReportButton type="product" id={p.id} label="Report this product" /></div>
        </div>
      </div>
    </Page>
  );
}

export function CartPage() {
  const { lines, remove, setQty, clear, subtotal, shipping } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [addr, setAddr] = useState({ name: "", line1: "", city: "", state: "", postal_code: "", country: "US" });
  const [busy, setBusy] = useState(false);
  const physical = lines.some((l) => l.kind === "physical");
  const checkout = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return nav("/login?next=/cart");
    setBusy(true);
    try {
      const { data: o } = await api.post("/orders", { items: lines.map((l) => ({ product_id: l.product_id, quantity: l.quantity, variant: l.variant || null })), shipping_address: physical ? addr : null });
      const { data: c } = await api.post("/payments/checkout", { kind: "order", ref_id: o.id, origin_url: window.location.origin });
      window.location.href = c.checkout_url;
    } catch (er) { toast.error(errMsg(er)); setBusy(false); }
  };
  if (lines.length === 0) return <Page className="max-w-2xl"><h1 className="text-4xl font-semibold mb-6">Cart</h1><Empty title="Your cart is empty." hint="Browse the Market to find tools and guides." /><Link to="/market" className="btn-wine mt-6">Go to Market</Link></Page>;
  return (
    <Page className="max-w-4xl">
      <h1 className="text-4xl font-semibold mb-1">Cart</h1>
      <p className="mono text-xs text-[#5E5C56] mb-6">Shop: {lines[0].reader_name} · one seller per checkout</p>
      <div className="grid md:grid-cols-[1.3fr_1fr] gap-8">
        <div className="space-y-3">
          {lines.map((l) => (
            <div key={l.product_id + (l.variant || "")} className="card-tactile p-3 flex gap-3 items-center" data-testid={`cart-line-${l.product_id}`}>
              <div className="h-16 w-16 rounded bg-[#1A1917] overflow-hidden shrink-0">{l.image_url && <img src={l.image_url} alt="" className="h-full w-full object-cover" />}</div>
              <div className="flex-1 min-w-0"><p className="serif text-lg leading-tight truncate">{l.title}</p><p className="mono text-[10px] text-[#5E5C56]">{l.kind}{l.variant ? ` · ${l.variant}` : ""} · {money(l.price)}</p></div>
              <div className="flex items-center gap-1"><button onClick={() => setQty(l.product_id, l.variant, l.quantity - 1)} className="p-1.5 border border-[#D8D4C9] rounded-full"><Minus size={12} /></button><span className="mono text-sm w-6 text-center" data-testid={`qty-${l.product_id}`}>{l.quantity}</span><button onClick={() => setQty(l.product_id, l.variant, l.quantity + 1)} className="p-1.5 border border-[#D8D4C9] rounded-full"><Plus size={12} /></button></div>
              <button onClick={() => remove(l.product_id, l.variant)} className="p-2 text-[#5E5C56] hover:text-[#581C25]" data-testid={`remove-${l.product_id}`}><Trash2 size={16} /></button>
            </div>))}
          <button onClick={clear} className="text-xs text-[#5E5C56] underline" data-testid="clear-cart">Clear cart</button>
        </div>
        <form onSubmit={checkout} className="card-tactile p-6 h-fit space-y-3" data-testid="checkout-panel">
          {physical && (<>
            <p className="eyebrow">Shipping address</p>
            <input className="input-ed" placeholder="Full name" required value={addr.name} onChange={(e) => setAddr({ ...addr, name: e.target.value })} data-testid="ship-name" />
            <input className="input-ed" placeholder="Address line" required value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} data-testid="ship-line1" />
            <div className="grid grid-cols-2 gap-2"><input className="input-ed" placeholder="City" required value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} data-testid="ship-city" /><input className="input-ed" placeholder="State" value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-2"><input className="input-ed" placeholder="Postal code" required value={addr.postal_code} onChange={(e) => setAddr({ ...addr, postal_code: e.target.value })} data-testid="ship-postal" /><input className="input-ed" placeholder="Country" required value={addr.country} onChange={(e) => setAddr({ ...addr, country: e.target.value })} /></div>
          </>)}
          <div className="hairline pt-3 text-sm space-y-1">
            <div className="flex justify-between"><span>Subtotal</span><span className="mono">{money(subtotal)}</span></div>
            <div className="flex justify-between"><span>Shipping</span><span className="mono">{money(shipping)}</span></div>
            <div className="flex justify-between font-semibold text-base pt-1"><span>Total</span><span className="mono" data-testid="cart-total">{money(subtotal + shipping)}</span></div>
          </div>
          <button className="btn-wine w-full" disabled={busy} data-testid="checkout-btn">{busy ? "Opening checkout…" : "Checkout with Stripe"}</button>
        </form>
      </div>
    </Page>
  );
}

export function PaymentResult() {
  const [sp] = useSearchParams();
  const { clear } = useCart();
  const [state, setState] = useState<"pending" | "paid" | "failed" | "expired">("pending");
  const sid = sp.get("session_id");
  const kind = sp.get("kind");
  const ref = sp.get("ref");
  useEffect(() => {
    if (!sid) return;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { setState("paid"); if (kind === "order") clear(); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState(data.payment_status); return; }
      } catch {}
      if (++tries < 8) setTimeout(poll, 2000); else setState("expired");
    };
    poll();
  }, [sid]);
  return (
    <Page className="max-w-xl text-center py-20" >
      {state === "pending" && <><p className="eyebrow mb-3">Confirming</p><h1 className="text-4xl font-semibold animate-pulse" data-testid="payment-pending">Confirming your payment…</h1></>}
      {state === "paid" && <>
        <p className="eyebrow mb-3 !text-[#2D5A3F]">Paid</p>
        <h1 className="text-4xl sm:text-5xl font-semibold" data-testid="payment-success">{kind === "booking" ? "Your reading room is open." : kind === "circle" ? "You're in the Circle." : "Order confirmed."}</h1>
        <p className="text-[#5E5C56] mt-4">{kind === "booking" ? "Your reader has been notified. Everything about this reading lives in your archive." : kind === "circle" ? "Member-only posts are unlocked and your badge is live. Renews monthly; cancel any time from You → Circles." : "Digital items are in your Library. Physical items will ship from the reader."}</p>
        <div className="flex justify-center gap-3 mt-8">
          {kind === "booking" ? <Link to={`/readings/${ref}`} className="btn-wine" data-testid="goto-reading-room">Enter reading room</Link> : kind === "circle" ? <Link to="/you?tab=circles" className="btn-wine" data-testid="goto-circles">My Circles</Link> : <Link to="/you?tab=library" className="btn-wine" data-testid="goto-library">Open Library</Link>}
          <Link to="/" className="btn-ghost">Back to Discover</Link>
        </div>
      </>}
      {(state === "failed" || state === "expired") && <><h1 className="text-4xl font-semibold" data-testid="payment-failed">Payment not completed.</h1><p className="text-[#5E5C56] mt-4">Nothing was charged. You can try again.</p><Link to="/" className="btn-ghost mt-8">Back</Link></>}
    </Page>
  );
}

export function PaymentCancel() {
  return <Page className="max-w-xl text-center py-20"><h1 className="text-4xl font-semibold" data-testid="payment-cancel">Checkout cancelled.</h1><p className="text-[#5E5C56] mt-4">No charge was made.</p><Link to="/" className="btn-ghost mt-8">Back to Discover</Link></Page>;
}
